import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NonIdealState } from '@blueprintjs/core';

import { Button } from 'components/buttons';
import { Collapsable, ShowHide } from 'components/layout';

import {
    formatCode,
    formatMode,
    formatSide,
    formatSizeBucket,
    formatValue,
    readFailedHeading,
    readFailedMessage,
    retryText
} from './diffViewerFormatting';

const noGroupsHeading = "No differences";
const noGroupsMessage = "No differences match these filters over this period.";

const defaultArray = [];
const defaultFunction = () => null;

function buildGroupTitle(group) {
    return [
        formatMode(group.mode),
        formatCode(group.code, group.subCode),
        formatValue(group.fieldName),
        formatSide(group.side),
        formatSizeBucket(group.sizeBucket)
    ]
        .filter((part) => part !== "")
        .join(" · ");
}

// Largest first, because the biggest group is usually the one cause worth chasing next.
function sortLargestFirst(groups) {
    return [...groups].sort((first, second) => second.count - first.count);
}

/**
 * The differences grouped by what they have in common, one group open at a time. The open group's
 * rows are passed in as children. A failed read is shown as such, never as an empty result.
 */
export function DiffViewerGroups(
    {
        groups = defaultArray,
        openSignature = null,
        loading = false,
        failed = false,
        onToggleGroup = defaultFunction,
        onRetry = defaultFunction,
        children
    }
) {
    return (
        <ShowHide
            evaluator={failed}
            show={(
                <div className="list-table-no-results" id="rules-engine-diff-groups-failed">
                    <NonIdealState
                        title={readFailedHeading}
                        description={readFailedMessage}
                        action={(
                            <Button id="rules-engine-diff-groups-retry" text={retryText} icon="refresh" onClick={onRetry} />
                        )}
                    />
                </div>
            )}
            hide={(
                <ShowHide
                    evaluator={groups.length === 0 && !loading}
                    show={(
                        <div className="list-table-no-results" id="rules-engine-diff-no-groups">
                            <NonIdealState title={noGroupsHeading} description={noGroupsMessage} />
                        </div>
                    )}
                    hide={(
                        <div id="rules-engine-diff-groups" className={classNames({ "bp6-skeleton": loading })}>
                            {sortLargestFirst(groups).map((group, index) => (
                                <div className="spacer-bottom-small" id={`rules-engine-diff-group-${index}`} key={group.signature}>
                                    <Collapsable
                                        title={buildGroupTitle(group)}
                                        metadata={`${group.count} ${group.count === 1 ? "difference" : "differences"}`}
                                        fullWidth
                                        expanded={group.signature === openSignature}
                                        onClick={() => onToggleGroup(group.signature)}
                                    >
                                        <ShowHide
                                            evaluator={group.signature === openSignature}
                                            show={(
                                                <div className="collapsable-content">{children}</div>
                                            )}
                                        />
                                    </Collapsable>
                                </div>
                            ))}
                        </div>
                    )}
                />
            )}
        />
    );
}

DiffViewerGroups.propTypes = {
    groups: PropTypes.array,
    openSignature: PropTypes.string,
    loading: PropTypes.bool,
    failed: PropTypes.bool,
    onToggleGroup: PropTypes.func,
    onRetry: PropTypes.func,
    children: PropTypes.node
};
